import React, { useEffect, useState } from 'react';

import {
    Heading,
    Container,
    Row,
    Col,
    Text,
    className,
    Spinner,
    isMobile,
} from 'aplazame-design-system';

import HubspotForm from '../HubspotForm';
import webpIsSupported from 'aplazame-design-system/src/utils/webpIsSupported';

import style from './style.module.scss';
import sectionStyle from 'aplazame-design-system/src/scss/section.module.scss';
import colorStyle from 'aplazame-design-system/src/scss/color.module.scss';
import textStyle from 'aplazame-design-system/src/scss/text.module.scss';
import transitionStyle from 'aplazame-design-system/src/scss/transitions.module.scss';

function FormSpinner() {
    return (
        <div
            {...className(
                `${style.loader} d-flex justify-content-center align-items-center`
            )}
        >
            <Spinner size="2.5rem" color="primary" borderSmall={false} />
        </div>
    );
}

export default function HeroFormSection({
    id,
    title,
    titleTag,
    pretitle,
    pretitleTag,
    text,
    textClassName,
    hubspotFormId,
    hubspotPortalId,
    order,
    isLast,
    colorScheme,
    gtmEventName,
    previousSectionType,
    customForm,
    customFormLoading,
    bgImage,
    bgImageMobile,
    bgImageWebp,
    bgImageMobileWebp,
}) {
    colorScheme = colorScheme?.toLowerCase();
    const isFirstLoad =
        typeof window !== 'undefined' &&
        !window.document.body.dataset.navigating;
    const isFirstSection = order === 1;
    const firstDelay = isFirstSection ? (isFirstLoad ? 300 : 100) : 0;

    const [bgDesktopImageSrc, setBgDesktopImageSrc] = useState(null);
    const [bgMobileImageSrc, setBgMobileImageSrc] = useState(null);

    useEffect(() => {
        if (bgImage || bgImageWebp || bgImageMobile || bgImageMobileWebp) {
            webpIsSupported((webpSupport) => {
                const desktopImage =
                    webpSupport && bgImageWebp && bgImageWebp.file
                        ? bgImageWebp
                        : bgImage && bgImage.file
                            ? bgImage
                            : null;
                if (desktopImage) {
                    const img = new Image();
                    img.onload = () => {
                        setBgDesktopImageSrc(
                            desktopImage.file.childImageSharp.gatsbyImageData.images.fallback.src
                        );
                    };
                    img.src = desktopImage.file.childImageSharp.gatsbyImageData.images.fallback.src;
                }

                const mobileImage =
                    webpSupport && bgImageMobileWebp && bgImageMobileWebp.file
                        ? bgImageMobileWebp
                        : bgImageMobile && bgImageMobile.file
                            ? bgImageMobile
                            : null;
                if (mobileImage) {
                    const img = new Image();
                    img.onload = () => {
                        setBgMobileImageSrc(
                            mobileImage.file.childImageSharp.gatsbyImageData.images.fallback.src
                        );
                    };
                    img.src = mobileImage.file.childImageSharp.gatsbyImageData.images.fallback.src;
                }
            });
        }
    }, [
        bgImage,
        bgImageWebp,
        bgImageMobile,
        bgImageMobileWebp,
        setBgDesktopImageSrc,
        setBgMobileImageSrc,
    ]);

    return (
        <section
            aria-labelledby={id}
            {...className(
                [
                    order !== 1 && previousSectionType !== 'DatoCmsFaqSection'
                        ? `mt-10`
                        : '',
                    isLast ? 'mb-4' : 'mb-5 mb-lg-20',
                    sectionStyle['with-bg'],
                    sectionStyle['with-bg--none'],
                    sectionStyle['with-bg--alt'],
                    colorStyle[`with-bg--${colorScheme}`],
                ].join(' ')
            )}
        >
            <div
                data-sal="fade"
                data-sal-easing="ease-out-quart"
                data-sal-delay={`xs-none--lg-${firstDelay}`}
                className={`${sectionStyle.bg} ${colorStyle.bg}`}
                style={{
                    '--sal-delay': `${firstDelay}ms`,
                    backgroundImage:
                        bgImage && bgImage.file
                            ? `url("${bgDesktopImageSrc
                                ? bgDesktopImageSrc
                                : bgImage.file.childImageSharp.gatsbyImageData.images.fallback.src
                            }")`
                            : 'none',
                }}
            />
            {bgImageMobile && bgImageMobile.file ? (
                <div
                    {...className(
                        `d-lg-none ${sectionStyle.bg} ${colorStyle.bg}`
                    )}
                    data-sal="fade"
                    data-sal-easing="ease-out-quart"
                    style={{
                        '--sal-delay': `${firstDelay}ms`,
                        backgroundImage: `url("${bgMobileImageSrc
                            ? bgMobileImageSrc
                            : bgImageMobile.file.childImageSharp.gatsbyImageData.images.fallback.src
                            }")`,
                    }}
                ></div>
            ) : null}
            <Container
                className={style.content}
                pt={{ xs: 10 }}
                pb={{ lg: 10 }}
            >
                <Row className="align-items-lg-center">
                    <Col pb={{ xs: 5, lg: 0 }} col={{ lg: 6 }}>
                        {pretitle ? (
                            <Heading
                                tag={pretitleTag}
                                className={`mb-2 text-primary ${style.preTitle}`}
                                data-sal={`slide-up${isFirstSection ? '' : '--lg-right'
                                    }`}
                                data-sal-easing="ease-out-quart"
                                style={
                                    isFirstSection
                                        ? {
                                            '--sal-delay': `${firstDelay + 300
                                                }ms`,
                                        }
                                        : {}
                                }
                                data-sal-delay={
                                    !isFirstSection
                                        ? `xs-none--lg-${firstDelay + 300}`
                                        : ''
                                }
                            >
                                {pretitle}
                            </Heading>
                        ) : null}
                        <Heading
                            id={id}
                            tag={titleTag}
                            className={`mb-6 pr-lg-3 ${style.heading}`}
                            data-sal={`slide-up${isFirstSection ? '' : '--lg-right'
                                }`}
                            data-sal-easing="ease-out-quart"
                            style={
                                isFirstSection
                                    ? {
                                        '--sal-delay': `${firstDelay +
                                            (pretitle ? 500 : 300)
                                            }ms`,
                                    }
                                    : {}
                            }
                            data-sal-delay={
                                !isFirstSection
                                    ? `xs-none--lg-${firstDelay + (pretitle ? 500 : 300)
                                    }`
                                    : ''
                            }
                        >
                            {title}
                        </Heading>
                        {text ? (
                            <Text
                                tag="div"
                                className={`${textClassName ? textClassName : 'h3'
                                    } mb-5 mb-lg-0 pr-lg-3 ${transitionStyle['child-link-underline']
                                    } ${textStyle['with-list']} ${style.text}`}
                                data-sal={`slide-up${isFirstSection ? '' : '--lg-right'
                                    }`}
                                data-sal-easing="ease-out-quart"
                                style={
                                    isFirstSection
                                        ? {
                                            '--sal-delay': `${firstDelay +
                                                (pretitle ? 700 : 500)
                                                }ms`,
                                        }
                                        : {}
                                }
                                data-sal-delay={
                                    !isFirstSection
                                        ? `xs-none--lg-${firstDelay +
                                        (pretitle ? 700 : 500)
                                        }`
                                        : ''
                                }
                            >
                                {text}
                            </Text>
                        ) : null}
                    </Col>
                    <Col
                        col={{ lg: 6 }}
                        className={`${style['col-form']} ${style.wrapperMobile}`}
                        data-sal={
                            isFirstSection
                                ? 'slide-up--lg-fade'
                                : 'slide-up--lg-left'
                        }
                        data-sal-easing="ease-out-quart"
                        data-sal-delay={`xs-none--lg-${firstDelay + (pretitle ? 700 : 500)
                            }`}
                    >
                        {customForm ? (
                            <div
                                className={`${style['custom-form']} ${transitionStyle['child-link-underline']}`}
                                data-sal={
                                    isFirstSection
                                        ? 'slide-up--lg-fade'
                                        : 'slide-up--lg-left'
                                }
                                data-sal-easing="ease-out-quart"
                                data-sal-delay={`xs-none--lg-${firstDelay + (pretitle ? 700 : 500)
                                    }`}
                            >
                                <div
                                    {...className(
                                        `${customFormLoading ? 'd-none' : ''}`
                                    )}
                                >
                                    {customForm}
                                </div>
                                {customFormLoading ? <FormSpinner /> : null}
                            </div>
                        ) : (
                            <div className={style.form}>
                                {typeof window !== 'undefined' &&
                                    hubspotFormId &&
                                    hubspotPortalId ? (

                                    <HubspotForm
                                        portalId={hubspotPortalId}
                                        formId={hubspotFormId}
                                        gtmEventName={gtmEventName}
                                        loading={<FormSpinner />}
                                    />


                                ) : (
                                    <FormSpinner />
                                )}
                            </div>
                        )}
                    </Col>
                </Row>
            </Container>
        </section>
    );
}
